import {
  RECIPE_CONFIDENCES,
  type AdminFoodDetail,
  type AdminFoodRecipeLineRequest,
  type AdminFoodRecipeUpdateRequest,
  type RecipeConfidenceName,
} from '@steado/admin-contract/foods';
import { useMutation } from '@tanstack/react-query';
import { PlusIcon, XIcon } from 'lucide-react';
import { useId, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth/auth-context';
import { Empty, Panel } from '@/components/panel/panels';
import { humanise } from '@/lib/format';

import { deriveFoodRecipe, updateFoodRecipe } from '@/screens/foods/foods-query';
import { IngredientPicker } from '@/screens/foods/IngredientPicker';

type RecipeView = NonNullable<AdminFoodDetail['recipe']>;

interface RecipePanelProps {
  readonly food: AdminFoodDetail;
  readonly canMutate: boolean;
  readonly onMutated: (food: AdminFoodDetail) => void;
}

interface LineDraft {
  rawName: string;
  ingredientFoodId: string | null;
  ingredientName: string | null;
  grams: string;
  retentionGroup: string;
}

const EMPTY_LINE: LineDraft = {
  rawName: '',
  ingredientFoodId: null,
  ingredientName: null,
  grams: '',
  retentionGroup: '',
};

function draftsFromRecipe(recipe: RecipeView | null): LineDraft[] {
  if (recipe === null || recipe.lines.length === 0) return [{ ...EMPTY_LINE }];
  return recipe.lines.map((line) => ({
    rawName: line.rawName,
    ingredientFoodId: line.ingredientFoodId,
    ingredientName: line.ingredientName,
    grams: String(line.grams),
    retentionGroup: line.retentionGroup ?? '',
  }));
}

function toRequestLines(drafts: LineDraft[]): AdminFoodRecipeLineRequest[] | null {
  const lines: AdminFoodRecipeLineRequest[] = [];
  for (const draft of drafts) {
    const rawName = draft.rawName.trim();
    const grams = Number(draft.grams);
    if (rawName === '' || !Number.isFinite(grams) || grams <= 0) return null;
    lines.push({
      rawName,
      ingredientFoodId: draft.ingredientFoodId,
      grams,
      retentionGroup: draft.retentionGroup.trim() === '' ? null : draft.retentionGroup.trim(),
    });
  }
  return lines.length === 0 ? null : lines;
}

/**
 * A dish's recipe: the lines derivation reads, and the diet-class/allergen
 * safety fields those lines produce (see recipe-derivation.ts). Always
 * rendered, even with no recipe yet — a dish with none can only be given one
 * from here.
 */
export function RecipePanel({ food, canMutate, onMutated }: RecipePanelProps) {
  const { api } = useAuth();
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<LineDraft[]>(() => draftsFromRecipe(food.recipe));
  const [confidence, setConfidence] = useState<RecipeConfidenceName>(food.recipe?.confidence ?? 'medium');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: (body: AdminFoodRecipeUpdateRequest) => updateFoodRecipe(api, food.id, body),
    onSuccess: (updated) => {
      setEditing(false);
      onMutated(updated);
    },
    retry: false,
  });

  const derive = useMutation({
    mutationFn: () => deriveFoodRecipe(api, food.id),
    onSuccess: onMutated,
    retry: false,
  });

  const requestLines = toRequestLines(drafts);
  const canSave = requestLines !== null && !save.isPending;

  const openEditor = () => {
    setDrafts(draftsFromRecipe(food.recipe));
    setConfidence(food.recipe?.confidence ?? 'medium');
    setNote('');
    save.reset();
    setEditing(true);
  };

  return (
    <Panel
      title={food.recipe === null ? 'Recipe' : `Recipe (v${String(food.recipe.version)})`}
      description="What derives this dish's allergens and diet class. See Diet classification above for the result."
      action={
        !canMutate || editing ? undefined : (
          <div className="flex items-center gap-2">
            {food.recipe === null ? null : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  derive.mutate();
                }}
                disabled={derive.isPending}
              >
                Re-derive
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={openEditor}>
              {food.recipe === null ? (
                <>
                  <PlusIcon aria-hidden="true" />
                  Add recipe
                </>
              ) : (
                'Edit'
              )}
            </Button>
          </div>
        )
      }
    >
      {editing ? (
        <RecipeEditor
          drafts={drafts}
          onChangeDrafts={setDrafts}
          confidence={confidence}
          onChangeConfidence={setConfidence}
          note={note}
          onChangeNote={setNote}
          canSave={canSave}
          isPending={save.isPending}
          error={save.isError ? save.error.message : null}
          onSubmit={() => {
            if (requestLines === null) return;
            save.mutate({
              lines: requestLines,
              confidence,
              ...(note.trim() === '' ? {} : { note: note.trim() }),
            });
          }}
          onCancel={() => {
            setEditing(false);
            save.reset();
          }}
        />
      ) : food.recipe === null ? (
        <Empty
          title="No recipe"
          body="Without one, this dish's allergens and diet class are whatever the importer guessed. Add one to derive them from real ingredients."
        />
      ) : (
        <RecipeSummary recipe={food.recipe} />
      )}

      {derive.isError ? (
        <p role="alert" className="mt-3 text-[13px] text-err-fg">
          {derive.error.message}
        </p>
      ) : null}
    </Panel>
  );
}

function RecipeSummary({ recipe }: { readonly recipe: RecipeView }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="mute">{humanise(recipe.source)}</Badge>
        <Badge tone={recipe.status === 'active' ? 'ok' : 'mute'}>{recipe.status}</Badge>
        <Badge tone="mute">{humanise(recipe.confidence)} confidence</Badge>
        <Badge tone={recipe.coverage === 1 ? 'ok' : 'warn'}>coverage {recipe.coverage.toFixed(2)}</Badge>
      </div>
      <p className="text-[13px] text-muted-foreground">
        {recipe.reviewer === null
          ? 'Unreviewed — an LLM decomposition. It can only derive allergens, never a diet class.'
          : `Reviewed by ${recipe.reviewer}.`}
      </p>
      <ul className="flex flex-col divide-y divide-border">
        {recipe.lines.map((line) => (
          <li key={line.id} className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0 text-sm">
            <span className="min-w-0 flex-1 truncate">
              <span className={line.ingredientName === null ? 'text-warn-fg' : 'text-foreground'}>
                {line.ingredientName ?? line.rawName}
              </span>
              {line.retentionGroup === null ? null : (
                <span className="ml-2 font-mono text-xs text-muted-foreground">{line.retentionGroup}</span>
              )}
            </span>
            <span className="shrink-0 font-mono text-[13px] text-muted-foreground">
              {String(line.grams)} g
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RecipeEditorProps {
  readonly drafts: LineDraft[];
  readonly onChangeDrafts: (next: LineDraft[]) => void;
  readonly confidence: RecipeConfidenceName;
  readonly onChangeConfidence: (next: RecipeConfidenceName) => void;
  readonly note: string;
  readonly onChangeNote: (next: string) => void;
  readonly canSave: boolean;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

function RecipeEditor({
  drafts,
  onChangeDrafts,
  confidence,
  onChangeConfidence,
  note,
  onChangeNote,
  canSave,
  isPending,
  error,
  onSubmit,
  onCancel,
}: RecipeEditorProps) {
  const confidenceId = useId();
  const noteId = useId();

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-4">
      <ul className="flex flex-col gap-3">
        {drafts.map((draft, index) => (
          <li key={index}>
            <LineRow
              draft={draft}
              onChange={(next) => {
                onChangeDrafts(drafts.map((d, i) => (i === index ? next : d)));
              }}
              onRemove={() => {
                onChangeDrafts(drafts.filter((_d, i) => i !== index));
              }}
              removable={drafts.length > 1}
            />
          </li>
        ))}
      </ul>

      <Button
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => {
          onChangeDrafts([...drafts, { ...EMPTY_LINE }]);
        }}
      >
        <PlusIcon aria-hidden="true" />
        Add ingredient
      </Button>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={confidenceId}>Confidence</Label>
          <Select
            value={confidence}
            onValueChange={(next) => {
              onChangeConfidence(next as RecipeConfidenceName);
            }}
          >
            <SelectTrigger id={confidenceId} className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RECIPE_CONFIDENCES.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
          <Label htmlFor={noteId}>Note (optional)</Label>
          <Textarea
            id={noteId}
            value={note}
            onChange={(event) => {
              onChangeNote(event.target.value);
            }}
            rows={2}
          />
        </div>
      </div>

      <p className="text-[13px] leading-relaxed text-muted-foreground">
        Saving mints a new version and retires the current one, then re-derives allergens and diet class from
        these lines. Leaving a line unmatched to a catalog ingredient is legal — it just refuses derivation,
        the same as an unresolved model guess.
      </p>

      <div className="flex items-center gap-2">
        <Button disabled={!canSave} onClick={onSubmit}>
          Save
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        {error === null ? null : (
          <p role="alert" className="text-[13px] text-err-fg">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

interface LineRowProps {
  readonly draft: LineDraft;
  readonly onChange: (next: LineDraft) => void;
  readonly onRemove: () => void;
  readonly removable: boolean;
}

function LineRow({ draft, onChange, onRemove, removable }: LineRowProps) {
  const rawNameId = useId();
  const gramsId = useId();
  const retentionId = useId();

  return (
    <div className="flex flex-wrap items-end gap-2.5 rounded-md border border-border p-2.5">
      <div className="flex min-w-[160px] flex-1 flex-col gap-1">
        <Label htmlFor={rawNameId}>Raw name</Label>
        <Input
          id={rawNameId}
          value={draft.rawName}
          onChange={(event) => {
            onChange({ ...draft, rawName: event.target.value });
          }}
        />
      </div>

      <div className="flex min-w-[200px] flex-1 flex-col gap-1">
        <Label>Catalog ingredient</Label>
        <IngredientPicker
          value={
            draft.ingredientFoodId === null
              ? null
              : { id: draft.ingredientFoodId, name: draft.ingredientName ?? draft.rawName }
          }
          onChange={(ingredient) => {
            onChange({
              ...draft,
              ingredientFoodId: ingredient?.id ?? null,
              ingredientName: ingredient?.name ?? null,
            });
          }}
        />
      </div>

      <div className="flex w-24 flex-col gap-1">
        <Label htmlFor={gramsId}>Grams</Label>
        <Input
          id={gramsId}
          value={draft.grams}
          inputMode="decimal"
          onChange={(event) => {
            onChange({ ...draft, grams: event.target.value });
          }}
          className="text-right font-mono"
        />
      </div>

      <div className="flex w-36 flex-col gap-1">
        <Label htmlFor={retentionId}>Retention group</Label>
        <Input
          id={retentionId}
          value={draft.retentionGroup}
          onChange={(event) => {
            onChange({ ...draft, retentionGroup: event.target.value });
          }}
        />
      </div>

      {removable ? (
        <Button variant="ghost" size="sm" onClick={onRemove} title="Remove this line">
          <XIcon aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}
